﻿import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { eScrollMode, Utils } from "@tools";
import * as angular from "angular";
import Tether, { ITetherConstraint } from "tether";

interface IDropdownScope extends ng.IScope {
    open?: boolean;
    scrollTo?: string;
    autoDirection?: boolean;
    horizontalAlign: string; // left, right, or undefined
    verticalAlign: string; // top, bottom, or undefined.  Defaults to bottom
    height: string; // defaults to auto
    width: string; // defaults to auto
    animate: boolean;
    ignoreClickOn: string; // selector of an element(s) that when clicked should not result in the dropdown closing
    ignoreClickOnParent: string; // same as above, but selector for parents of elements that are clicked on
}

@NgComponent({
    token: Token.KbDropdown,
    dependencies: [
        Token.$Timeout
    ]
})
export class KbDropdown extends Directive {
    constructor($timeout: ng.ITimeoutService) {
        super();

        this.restrict = "EA";
        this.scope = {
            open: "=",
            scrollTo: "@",
            autoDirection: "=",
            horizontalAlign: "@",
            verticalAlign: "@",
            height: "@",
            width: "@",
            animate: "@",
            ignoreClickOn: "@",
            ignoreClickOnParent: "@"
        };
        this.replace = true;
        this.transclude = true;
        this.template = `
<div class='kb-dropdown'>
    <div class='kb-dropdown__overlay'></div>
    <div class='kb-dropdown__content' ng-transclude></div>
</div>`;

        this.link = (scope: IDropdownScope, element: JQuery, atts: any) => {
            let timeouts: ng.IPromise<any>[] = [];
            let guid = Utils.shortId();
            let $parent = $(element).parent();
            let openIsDefined = angular.isDefined(atts.open);
            let hAlign = scope.horizontalAlign || "left";
            let vAlign = scope.verticalAlign || "bottom";
            let vAlignOpposite = vAlign == "top" ? "bottom" : "top";
            let isVisible = false;

            // if height is set, make the dropdown scrollable
            if (scope.height && scope.height != 'auto') element.addClass("kb-dropdown--scrollable");
            let height = scope.height || "auto";            
            element.css("max-height", height);
            

            // define a tether
            let tether: Tether;
            let createTether = () => {
                if (!tether) {
                    let options = {
                        element,
                        target: $parent,
                        attachment: vAlignOpposite + " " + hAlign,
                        targetAttachment: vAlign + " " + hAlign,
                        constraints: [
                            {
                                to: document as any,
                                attachment: "together",
                                pin: ["left","right"]
                            } as ITetherConstraint
                        ],
                        classes: {
                            tether: false,
                            element: false,
                            target: false,
                            enabled: false,
                            // 'element-attached': false,
                            // 'target-attached': false,
                            // 'abutted': false
                        }
                    } as Tether.ITetherOptions;
                    
                    // if the dropdown is part of a hotspot, and we are in XR, then we need to add to the hotspot div instead of body
                    let p = element[0].closest(".xr-overlay") as HTMLElement;
                    if (p){
                        options.bodyElement = p;
                    }
                    
                    tether = new Tether(options);
                }
            };

            let scrollToActive = () => {
                if (isVisible && scope.scrollTo && scope.scrollTo.length > 1) {
                    let $item = element.find(scope.scrollTo);
                    if ($item.length > 0) {
                        Utils.scrollIntoView({ elem: $item[0], mode: eScrollMode.middleIfNecessary, animate: false });
                    }
                }
            };

            scope.$watch("scrollTo", newValue => {
                scrollToActive();
            });

            let preventScroll = (e: Event) => {
                if (e.target == document.body) e.preventDefault();
            };

            let hideDropdown = () => {
                if (!isVisible) return;
                isVisible = false;

                //stage the close so CSS can animate it
                $parent.removeClass("is-open");
                element.removeClass("is-open");

                setTimeout(() => {
                    element.css("display", "none");
                }, 366)

                document.removeEventListener("click", onDocumentClicked, true);
                document.body.removeEventListener("touchmove", preventScroll);
            };

            // let hideDropdown = () => {
            //     if (!isVisible) return;
            //     isVisible = false;
            //     // setting to false so that in configurators, we're not waiting for the dropdown 
            //     // to close while the scene is being updated.Makes it feel sluggish
            //     // var animate = false; //!element.hasClass("tether-element-attached-bottom");
            //     let afterAnimation = () => {
            //         element.css("display", "none");
            //         element.css("height", "auto");
            //         $parent.removeClass("is-open");
            //     };

            //     if (scope.animate) {
            //         element.animate({ height: 0 }, 133, "easeOutSine", afterAnimation);
            //     } else {
            //         afterAnimation();
            //         // $timeout(afterAnimation, 0);
            //     }

            //     document.removeEventListener("click", onDocumentClicked, true);
            //     // $("body").removeClass("is-mobile-keyboard").css("overflow-y", "auto");
            //     document.body.removeEventListener("touchmove", preventScroll);
            // };

            let onDocumentClicked = (e: Event) => {
                let $target = $(e.target);
                if (
                    $target.hasClass("kb-dropdown__overlay")
                    || (
                        !$target.is(scope.ignoreClickOn) &&
                        !$target.is($parent) &&
                        !$.contains($parent[0], e.target as Element) &&
                        !$target.hasClass("kb-dropdown--ignore-click") &&
                        !$target.parents(".kb-dropdown--ignore-click").length &&
                        (!scope.ignoreClickOnParent || !$target.parents(scope.ignoreClickOnParent).length)
                    )
                ) {
                    scope.$apply(() => {
                        hideDropdown();
                        if (openIsDefined) {
                            scope.open = false;
                        }
                    });
                }
            };

            let showDropdown = () => {
                if (isVisible) return;
                isVisible = true;

                // Workaround for a bug in tether that finds a "zero element" in the xr-overlay when in the document.body context, which throws positioning off.
                const tetherZeroElem = document.body.querySelector(".xr-overlay [data-tether-id]");
                if (tetherZeroElem) {
                    tetherZeroElem.parentElement.removeChild(tetherZeroElem);
                }
                
                createTether();
                
                let parentWidth = $parent[0].getBoundingClientRect().width;
                let maxWidth = scope.width == "parent"?  parentWidth: (scope.width || "auto");
                element.css("max-width", maxWidth);
                // make the dropdown at least the width of the parent
                element.css("min-width", $parent[0].getBoundingClientRect().width);
                element.css("display", "block");
                tether.position();

                scrollToActive();
                $parent.addClass("is-open");
                element.addClass("is-open");

                document.addEventListener("click", onDocumentClicked, true);
                $(document).on("touchmove", preventScroll);

                $timeout(() => tether.position(), 0);
            };

            // let showDropdown = () => {
            //     if (isVisible) return;
            //     isVisible = true;
            //     createTether();
            //     // make the dropdown at least the width of the parent
            //     element.css("min-width", $parent[0].getBoundingClientRect().width);
            //     $parent.addClass("is-open");
            //     element.css("display", "block");

            //     tether.position();

            //     // element.css("height", "auto");
            //     let autoHeight = element.height();
            //     // if we are attached to the bottom of the element, we don't want the
            //     // normal height animation, so we just show the dropdown
            //     let goingDown = !element.hasClass("tether-element-attached-bottom");

            //     let afterAnimation = () => {
            //         element.css("height", "auto");
            //         document.addEventListener("click", onDocumentClicked, true);
            //     };
            //     if (scope.animate && goingDown) {
            //         scrollToActive();
            //         element.height(0).animate({ height: autoHeight }, 133, "easeOutSine", afterAnimation);
            //     } else {                    
            //         timeouts.push($timeout(() => {
            //             scrollToActive();
            //             afterAnimation();
            //         }, 0));
            //     }

            //     // $("body").addClass("is-mobile-keyboard").css("overflow-y", "hidden");
            //     $(document).on("touchmove", preventScroll);

            // };

            let toggleDropdown = (e: JQueryEventObject) => {

                if ($parent.hasClass("is-open")) {
                    hideDropdown();
                } else {
                    showDropdown();
                }
                e.stopPropagation();
            };

            if (openIsDefined) {
                scope.$watch("open", newValue => {
                    if (newValue) {
                        showDropdown();
                    } else {
                        hideDropdown();
                    }
                });
            } else {
                $parent.on("click.kbdropdown", toggleDropdown);
            }

            scope.$on("$destroy", () => {
                timeouts.forEach(t => $timeout.cancel(t));
                document.removeEventListener("click", onDocumentClicked, true);
                $(document).off("touchmove", preventScroll);
                $parent.off("click.kbdropdown");
                // remove the dropdown element which is at the bottom of the body thanks to tether
                if (tether) tether.destroy();
                element.remove();
            });

        };
    }
}
